import {useState} from 'react';
import {RotateCw, X, ZoomIn, ZoomOut} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {Button} from '../button';
import {Image} from '../image';
import {useClickEscape} from '../hooks';

export interface ImageFullScreenViewProps {
  src: string;
  alt: string;
  onClose: () => void;
}

export function ImageFullScreenView({src, alt, onClose}: ImageFullScreenViewProps) {
  const {t} = useTranslation();
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  useClickEscape({onClick: onClose});

  const zoomIn = () => setScale(value => Math.min(value + 0.25, 3));
  const zoomOut = () => setScale(value => Math.max(value - 0.25, 0.5));
  const rotate = () => setRotation(value => (value + 90) % 360);

  return (
    <div className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black/90">
      <div className="absolute right-4 top-4 z-[301] flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={zoomIn}
          className="rounded-full text-white hover:bg-white/20"
          aria-label={t('zoom_in')}
        >
          <ZoomIn className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={zoomOut}
          className="rounded-full text-white hover:bg-white/20"
          aria-label={t('zoom_out')}
        >
          <ZoomOut className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={rotate}
          className="rounded-full text-white hover:bg-white/20"
          aria-label={t('rotate')}
        >
          <RotateCw className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="rounded-full text-white hover:bg-white/20"
          aria-label={t('close')}
        >
          <X className="h-6 w-6" />
        </Button>
      </div>

      <div
        className="flex h-full w-full cursor-move items-center justify-center overflow-hidden"
        onClick={event => {
          if (event.target === event.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          style={{
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-in-out',
          }}
          className="max-h-[90%] max-w-[90%]"
        >
          <Image
            src={src || ''}
            alt={alt}
            className="max-h-full max-w-full object-contain"
            width={1200}
            height={800}
          />
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-white/70">
        <p>{t('esc_to_close')}</p>
      </div>
    </div>
  );
}
